/**
 * The appearance contract: the global switches, their storage format, and
 * the hook that reads them.
 *
 * Handoff, "Global switches": `data-theme`, `data-density` and `dir` on
 * `<html>` are the ONLY global modes; nothing else forks. `lang` travels with
 * the locale because `dir` is derived from it, not chosen separately.
 *
 * This module holds no component, only the context, the hook and pure helpers.
 * That split is not stylistic: `eslint-plugin-react-refresh` (and Vite's Fast
 * Refresh itself) require a module that exports a component to export nothing
 * else, so the plan's single-file `AppearanceProvider.tsx` exporting both the
 * provider and `useAppearance` would break HMR and fail lint.
 *
 * It is also the shared vocabulary for the no-flash boot snippet in
 * `index.html`, which cannot import anything but must agree on
 * `APPEARANCE_STORAGE_KEY` and the accepted values. `appearanceBoot.test.ts`
 * executes that snippet against this constant to keep the two honest.
 */

import { createContext, useContext } from 'react';

import { backgroundUrl } from '../backgrounds';

export type Theme = 'dark' | 'light';
export type Density = 'comfortable' | 'compact';
export type Locale = 'en' | 'ar';
export type Direction = 'ltr' | 'rtl';

/**
 * The accent palette a user may pick from. A fixed set rather than a colour
 * picker: every entry has a full nine-step ramp in tokens.css whose light-mode
 * line and text steps were measured against WCAG (3:1 line, 4.5:1 text), and a
 * free picker could hand back a colour no ramp exists for. `yellow` is the
 * FreeStyle Libre mark's own yellow and the default. Coral and green were left
 * out on purpose: they would read as the escalated and closed status hues.
 */
export type Accent = 'yellow' | 'amber' | 'rose' | 'violet' | 'blue' | 'teal';

/**
 * The shell background. `none` is the plain ground; every other id names an
 * image in `public/backgrounds/`, mapped to a URL in backgrounds.css.
 */
export type Background =
  | 'none'
  | 'glucose-trace'
  | 'sensor-rings'
  | 'dunes'
  | 'contours'
  | 'aurora'
  | 'night-sky';

export const THEMES: readonly Theme[] = ['dark', 'light'];
export const DENSITIES: readonly Density[] = ['comfortable', 'compact'];
export const LOCALES: readonly Locale[] = ['en', 'ar'];
export const ACCENTS: readonly Accent[] = ['yellow', 'amber', 'rose', 'violet', 'blue', 'teal'];
export const BACKGROUNDS: readonly Background[] = [
  'none',
  'glucose-trace',
  'sensor-rings',
  'dunes',
  'contours',
  'aurora',
  'night-sky',
];

/** The persisted shape. `dir` is never stored — it is derived from `locale`. */
export interface AppearanceState {
  theme: Theme;
  density: Density;
  locale: Locale;
  accent: Accent;
  background: Background;
}

export interface Appearance extends AppearanceState {
  dir: Direction;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
  setLocale: (locale: Locale) => void;
  setAccent: (accent: Accent) => void;
  setBackground: (background: Background) => void;
}

export const APPEARANCE_STORAGE_KEY = 'fsh.appearance';

/**
 * Handoff, "State Management": theme defaults to 'dark', density to
 * 'comfortable'. Deliberately NOT seeded from `prefers-color-scheme` — see the
 * note in AppearanceProvider.tsx.
 */
export const DEFAULT_APPEARANCE: AppearanceState = {
  theme: 'dark',
  density: 'comfortable',
  locale: 'en',
  accent: 'yellow',
  background: 'dunes',
};

export function directionFor(locale: Locale): Direction {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown, fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * Coerces anything at all into a valid state. Stored preferences are untrusted
 * text that ends up inside an attribute selector, so every field is checked
 * against its enum and each field falls back independently — one corrupt value
 * must not discard the other two.
 */
export function normaliseAppearance(value: unknown): AppearanceState {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return DEFAULT_APPEARANCE;
  }

  const record = value as Record<string, unknown>;

  return {
    theme: oneOf(THEMES, record['theme'], DEFAULT_APPEARANCE.theme),
    density: oneOf(DENSITIES, record['density'], DEFAULT_APPEARANCE.density),
    locale: oneOf(LOCALES, record['locale'], DEFAULT_APPEARANCE.locale),
    accent: oneOf(ACCENTS, record['accent'], DEFAULT_APPEARANCE.accent),
    background: oneOf(BACKGROUNDS, record['background'], DEFAULT_APPEARANCE.background),
  };
}

export function readStoredAppearance(): AppearanceState {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    return raw === null ? DEFAULT_APPEARANCE : normaliseAppearance(JSON.parse(raw));
  } catch {
    /* Private browsing, a disabled storage partition, or malformed JSON. */
    return DEFAULT_APPEARANCE;
  }
}

export function writeStoredAppearance(state: AppearanceState): void {
  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* Private browsing or quota: the preference simply does not survive. */
  }
}

/** Stamps the global switches onto the document element. */
export function applyAppearance(
  state: AppearanceState,
  root: HTMLElement = document.documentElement,
): void {
  root.setAttribute('data-theme', state.theme);
  root.setAttribute('data-density', state.density);
  root.setAttribute('lang', state.locale);
  root.setAttribute('dir', directionFor(state.locale));
  root.setAttribute('data-accent', state.accent);
  root.setAttribute('data-background', state.background);
  // The chosen image as the one custom property every full-page ground reads
  // — the shell AND the signed-out screens — so a sign-in page shows the
  // same background the agent picked rather than its own. The URL comes from
  // `backgrounds.ts`, the single table the topbar picker also reads.
  const url = backgroundUrl(state.background);
  root.style.setProperty('--app-background-image', url === null ? 'none' : `url('${url}')`);
}

export const AppearanceContext = createContext<Appearance | null>(null);

export function useAppearance(): Appearance {
  const appearance = useContext(AppearanceContext);
  if (appearance === null) {
    throw new Error('useAppearance must be used inside an AppearanceProvider');
  }
  return appearance;
}
